import type { CoasterProject } from '../schema/index.ts';
import { validateFullRide, type FullRunValidationResult } from './full-run.ts';

interface WorkerRequest {
  readonly requestId: number;
  readonly project: CoasterProject;
}

interface WorkerResponse {
  readonly requestId: number;
  readonly ok: boolean;
  readonly result?: FullRunValidationResult;
  readonly error?: string;
}

interface PendingValidation {
  readonly project: CoasterProject;
  readonly resolve: (value: FullRunValidationResult) => void;
  readonly reject: (reason: Error) => void;
}

export class ValidationSupersededError extends Error {
  readonly code = 'validation_superseded';

  constructor() {
    super('Validation superseded by a newer edit.');
    this.name = 'ValidationSupersededError';
  }
}

export interface FullRunCoordinatorDiagnostics {
  readonly active: number;
  readonly queued: number;
  readonly pending: number;
}

/**
 * Runs at most one full simulation and retains only the newest replacement.
 * A worker request already executing cannot be interrupted synchronously, but
 * stale edits can no longer build an unbounded queue behind it on mobile.
 */
export class FullRunValidationCoordinator {
  private worker: Worker | null = null;
  private sequence = 0;
  private activeRequestId: number | null = null;
  private queuedRequestId: number | null = null;
  private pending = new Map<number, PendingValidation>();
  private disposed = false;

  constructor() {
    if (typeof Worker === 'undefined') return;
    if (
      import.meta.env.DEV &&
      typeof navigator !== 'undefined' &&
      /Firefox\//.test(navigator.userAgent)
    )
      return;
    try {
      this.worker = new Worker(new URL('./full-run.worker.ts', import.meta.url), {
        type: 'module',
      });
      this.worker.addEventListener('message', (event: MessageEvent<WorkerResponse>) => {
        const response = event.data;
        if (response.ok && response.result)
          this.complete(response.requestId, response.result, null);
        else
          this.complete(
            response.requestId,
            null,
            new Error(response.error ?? 'Full ride validation failed.'),
          );
      });
      this.worker.addEventListener('error', (event) => {
        event.preventDefault();
        this.fallBackFromWorker();
      });
    } catch {
      this.worker = null;
    }
  }

  get diagnostics(): FullRunCoordinatorDiagnostics {
    return {
      active: this.activeRequestId === null ? 0 : 1,
      queued: this.queuedRequestId === null ? 0 : 1,
      pending: this.pending.size,
    };
  }

  validate(project: CoasterProject): Promise<FullRunValidationResult> {
    if (this.disposed) return Promise.reject(new Error('Validation coordinator is disposed.'));
    const requestId = ++this.sequence;
    const candidate = structuredClone(project);
    return new Promise((resolve, reject) => {
      this.pending.set(requestId, { project: candidate, resolve, reject });
      if (this.activeRequestId === null) {
        this.start(requestId);
        return;
      }
      if (this.queuedRequestId !== null) this.rejectRequest(this.queuedRequestId);
      this.queuedRequestId = requestId;
    });
  }

  cancel(): void {
    this.sequence += 1;
    for (const requestId of [...this.pending.keys()]) this.rejectRequest(requestId);
    this.queuedRequestId = null;
  }

  dispose(): void {
    if (this.disposed) return;
    this.disposed = true;
    this.cancel();
    this.worker?.terminate();
    this.worker = null;
    this.activeRequestId = null;
  }

  private start(requestId: number): void {
    const request = this.pending.get(requestId);
    if (!request) return;
    this.activeRequestId = requestId;
    if (this.worker) {
      const message: WorkerRequest = { requestId, project: request.project };
      this.worker.postMessage(message);
      return;
    }
    void validateFullRide(structuredClone(request.project)).then(
      (result) => this.complete(requestId, result, null),
      (error: unknown) =>
        this.complete(
          requestId,
          null,
          error instanceof Error ? error : new Error('Full ride validation failed.'),
        ),
    );
  }

  private complete(
    requestId: number,
    result: FullRunValidationResult | null,
    error: Error | null,
  ): void {
    if (requestId !== this.activeRequestId) return;
    const request = this.pending.get(requestId);
    this.pending.delete(requestId);
    this.activeRequestId = null;
    if (request) {
      if (result) request.resolve(result);
      else request.reject(error ?? new Error('Full ride validation failed.'));
    }
    this.startQueued();
  }

  private startQueued(): void {
    const queued = this.queuedRequestId;
    this.queuedRequestId = null;
    if (queued !== null && this.pending.has(queued)) this.start(queued);
  }

  private rejectRequest(requestId: number): void {
    const request = this.pending.get(requestId);
    if (!request) return;
    this.pending.delete(requestId);
    request.reject(new ValidationSupersededError());
  }

  private fallBackFromWorker(): void {
    this.worker?.terminate();
    this.worker = null;
    const active = this.activeRequestId;
    this.activeRequestId = null;
    if (active !== null && this.pending.has(active)) this.start(active);
    else this.startQueued();
  }
}
